import { AcknowledgmentResponseStatus } from '../../../services/socket/SocketEvents';
import { setSaveStatusBasedOnApiResponse } from '../grid/reduxStore/saveStatusSlice';
import { updateBlockStylesViaSocket } from '../grid/reduxStore/saveHandlers.ts';
import type { BlockSettings } from '../grid/reduxStore/blockStyleSettingsSlice.ts';
import { updateBlockSettings } from '../grid/reduxStore/blockStyleSettingsSlice.ts';
import type { BlockCommandDependencies } from './BlockBaseCommand.ts';
import { BlockBaseCommand } from './BlockBaseCommand.ts';

export class UpdateBlockStylesCommand extends BlockBaseCommand {
  private readonly blockId: string;
  private readonly previousStyles: BlockSettings;
  private readonly newStyles: BlockSettings;

  constructor(blockId: string, previousStyles: BlockSettings, newStyles: BlockSettings, dependencies: BlockCommandDependencies) {
    super(dependencies);
    this.blockId = blockId;
    this.previousStyles = structuredClone(previousStyles);
    this.newStyles = structuredClone(newStyles);
  }

  /**
   * Execute - First application with optimistic update
   */
  async execute(): Promise<boolean> {
    try {
      this.setSaving();

      this.dispatch(
        updateBlockSettings({
          blockId: this.blockId,
          blockSettings: this.newStyles,
        })
      );

      const response = await updateBlockStylesViaSocket(this.socketClient, {
        blockId: this.blockId,
        userId: this.userId,
        styles: this.cleanStylesForPayload(this.newStyles, this.blockId),
      });

      if (this.checkDocumentLockStatus(response.errorCode)) {
        throw new Error('Document is locked');
      }

      if (response.status !== AcknowledgmentResponseStatus.OK) {
        throw new Error(response.errorCode || 'Style update rejected');
      }

      this.dispatch(
        setSaveStatusBasedOnApiResponse({
          status: response.status,
          errorCode: response.errorCode,
        })
      );

      return true;
    } catch (error) {
      this.dispatch(
        updateBlockSettings({
          blockId: this.blockId,
          blockSettings: this.previousStyles,
        })
      );
      console.error('Failed to execute style update:', error);
      this.setNotSaved();
      return false;
    }
  }

  async undo(): Promise<boolean> {
    return this.applyStyles(this.previousStyles);
  }

  async redo(): Promise<boolean> {
    return this.applyStyles(this.newStyles);
  }

  private async applyStyles(styles: BlockSettings): Promise<boolean> {
    try {
      this.setSaving();

      const response = await updateBlockStylesViaSocket(this.socketClient, {
        blockId: this.blockId,
        userId: this.userId,
        styles: this.cleanStylesForPayload(styles, this.blockId),
      });

      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.setNotSaved();
        return false;
      }

      if (response.status !== AcknowledgmentResponseStatus.OK) {
        console.error('Server rejected style update:', response);
        this.setNotSaved();
        return false;
      }

      this.dispatch(
        updateBlockSettings({
          blockId: this.blockId,
          blockSettings: styles,
        })
      );

      this.dispatch(
        setSaveStatusBasedOnApiResponse({
          status: response.status,
          errorCode: response.errorCode,
        })
      );

      return true;
    } catch (error) {
      console.error('Failed to apply styles:', error);
      this.setNotSaved();
      return false;
    }
  }

  getCurrentBlockId(): string {
    return this.blockId;
  }
}
